import React, {useCallback, useState, useRef} from "react";
import {useSelector, useDispatch} from "react-redux";
import {RootState} from "../../../../store/rootReducer";
import {useNavigate} from "@reach/router";
import styled from "styled-components";
import { ThemeProps } from "../../../../styles/theme";
import { searchActions } from "../../search.slice";
import { CardMarkDto } from "../../../../common";
import { markSummary } from "../../../mark/markInstructions";

interface Props {
    id: string;
    type: 'Card';
    title: string;
    subTitle: string;
    mark?: CardMarkDto;
}

const CardText = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
`;

const MarkText = styled.span<ThemeProps>`
    margin-top: 4px;
    font-size: 0.85em;
    color: ${props => props.theme.secondaryText};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const CardContainer = styled.div`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-bottom: 10px;

    &:first-child {
        margin-top: 10px;
    }
`;

interface CardProps extends ThemeProps {
    $isSelected?: boolean;
}

const Card = styled.div<CardProps>`
    display: flex;
    flex-direction: column;
    background-color: ${props => props.theme.primaryBackground};
    width: 80%;
    margin: 0 auto;
    border-radius: 4px;
    padding: 10px;
    box-sizing: border-box;
    border: ${(props: CardProps) => props.$isSelected === true 
        ? '2px solid #4285f4' 
        : `${props.theme.primaryBorder} 2px solid`};
    box-shadow: ${(props: CardProps) => props.$isSelected === true 
        ? '0 0 8px rgba(66, 133, 244, 0.5)' 
        : `4px 4px ${props.theme.cardShadow}`};
    position: relative;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    user-select: none; /* Предотвращает выделение текста */
    -webkit-user-select: none; /* Для Safari */
    -moz-user-select: none; /* Для Firefox */
    -ms-user-select: none; /* Для IE/Edge */
`;

const SelectionIndicator = styled.div`
    position: absolute;
    top: 10px;
    right: 10px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: #4285f4;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 12px;
`;


// Используем React.FC для правильной типизации
export const SearchCard: React.FC<Props> = (props) => {
    const {id, type} = props;
    const dispatch = useDispatch();
    const selectionSelector = useCallback((state: RootState) => ({
        isSelectionMode: state.search.isSelectionMode,
        isSelected: state.search.selectedIds.includes(id),
    }), [id]);

    const { isSelectionMode, isSelected } = useSelector(selectionSelector);
    const longPressTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const mouseDownTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [touchStartTime, setTouchStartTime] = useState<number>(0);
    const [mouseDownTime, setMouseDownTime] = useState<number>(0);
    const touchEndedRef = useRef<boolean>(false);
    const [touchStartY, setTouchStartY] = useState<number>(0);
    const isScrollingRef = useRef<boolean>(false);
    const lastSelectedCardIdRef = useRef<string | null>(null);

    const navigate = useNavigate();
    
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        touchEndedRef.current = false;
        isScrollingRef.current = false;
        setTouchStartTime(Date.now());
        setTouchStartY(e.touches[0].clientY);
        
        // Сохраняем ID карточки, на которой начался тач
        lastSelectedCardIdRef.current = id;
        
        // Если уже в режиме выбора, не устанавливаем таймер для лонг тапа
        if (!isSelectionMode) {
            longPressTimeoutRef.current = setTimeout(() => {
                // Проверяем, не начался ли скроллинг
                if (!isScrollingRef.current) {
                    dispatch(searchActions.toggleSelectionMode());
                    dispatch(searchActions.selectCard(id));
                }
            }, 500); // 500ms для лонг тапа
        }
    }, [dispatch, id, isSelectionMode]);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        const touchY = e.touches[0].clientY;
        const deltaY = Math.abs(touchY - touchStartY);
        
        // Если перемещение больше порога, считаем это скроллингом
        if (deltaY > 10) {
            isScrollingRef.current = true;
            
            // Отменяем таймер лонг тапа при скроллинге
            if (longPressTimeoutRef.current) {
                clearTimeout(longPressTimeoutRef.current);
                longPressTimeoutRef.current = null;
            }
        }
    }, [touchStartY]);

    const navigateToDetails = useCallback((id: string, cardType: "Card") => {
        // Навигация к деталям карточки
        if (cardType === "Card") {
            navigate(`/card/${id}`);
        }
    }, [navigate]);

    const handleTouchEnd = useCallback((e: React.TouchEvent) => {
        e.preventDefault(); // Предотвращаем стандартное поведение
        touchEndedRef.current = true; // Устанавливаем флаг, что touchend сработал
        
        if (longPressTimeoutRef.current) {
            clearTimeout(longPressTimeoutRef.current);
            longPressTimeoutRef.current = null;
        }
        
        // Если это был короткий тап (менее 500ms) и не было скроллинга
        const touchDuration = Date.now() - touchStartTime;
        if (touchDuration < 500 && !isScrollingRef.current) {
            if (isSelectionMode) {
                // В режиме выбора короткий тап переключает выбор карточки
                if (isSelected) {
                    dispatch(searchActions.unselectCard(id));
                } else {
                    dispatch(searchActions.selectCard(id));
                }
                
                // Важно: устанавливаем задержку перед сбросом флага touchEndedRef
                // чтобы предотвратить срабатывание click события
                setTimeout(() => {
                    touchEndedRef.current = false;
                }, 500); // Увеличиваем время до 500ms
                
                return; // Важно: прерываем выполнение функции здесь
            } else {
                // В обычном режиме короткий тап открывает карточку
                navigateToDetails(id, type);
            }
        }
        
        // Сбрасываем флаг через небольшую задержку
        setTimeout(() => {
            touchEndedRef.current = false;
        }, 300); // Достаточно времени, чтобы click не сработал
    }, [dispatch, isSelectionMode, isSelected, id, type, touchStartTime, navigateToDetails]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        setMouseDownTime(Date.now());
        
        // Если уже в режиме выбора или нажата клавиша Ctrl, не устанавливаем таймер для долгого нажатия
        if (!isSelectionMode && !e.ctrlKey) {
            mouseDownTimeoutRef.current = setTimeout(() => {
                dispatch(searchActions.toggleSelectionMode());
                dispatch(searchActions.selectCard(props.id));
            }, 500); // 500ms для долгого нажатия
        }
    }, [dispatch, props.id, isSelectionMode]);

    const handleMouseUp = useCallback((e: React.MouseEvent) => {
        if (mouseDownTimeoutRef.current) {
            clearTimeout(mouseDownTimeoutRef.current);
            mouseDownTimeoutRef.current = null;
        }
        
        // Если это было короткое нажатие (менее 500ms)
        const mouseDuration = Date.now() - mouseDownTime;
        if (mouseDuration < 500) {
            if (isSelectionMode || e.ctrlKey) {
                // В режиме выбора или при нажатой Ctrl короткое нажатие переключает выбор карточки
                e.preventDefault(); // Предотвращаем навигацию
                if (isSelected) {
                    dispatch(searchActions.unselectCard(props.id));
                } else {
                    dispatch(searchActions.selectCard(props.id));
                }
                
                // Если нажата Ctrl и мы не в режиме выбора, включаем режим выбора
                if (e.ctrlKey && !isSelectionMode) {
                    dispatch(searchActions.toggleSelectionMode());
                }
            } else {
                // В обычном режиме короткое нажатие открывает карточку
                // Обработка будет в handleClick
            }
        }
    }, [dispatch, isSelectionMode, isSelected, props.id, mouseDownTime]);

    const handleClick = useCallback((e: React.MouseEvent) => {
        // Если недавно был touchend, игнорируем click полностью
        if (touchEndedRef.current) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        
        // Если нажата клавиша Ctrl или в режиме выбора, обработка уже произошла в handleMouseUp
        if (e.ctrlKey || isSelectionMode) {
            e.preventDefault(); // Предотвращаем навигацию
            return;
        }
        
        navigateToDetails(id, type);
    }, [isSelectionMode, id, type, navigateToDetails]);

    const handleContextMenu = useCallback((e: React.MouseEvent) => {
        e.preventDefault(); // Предотвращаем стандартное контекстное меню
        
        // Если не в режиме выбора, включаем его
        if (!isSelectionMode) {
            dispatch(searchActions.toggleSelectionMode());
        }
        
        // Выбираем карточку, если она еще не выбрана
        if (!isSelected) {
            dispatch(searchActions.selectCard(id));
        }
    }, [dispatch, isSelectionMode, isSelected, id]);

    return (
        <CardContainer>
            <Card
                $isSelected={isSelected}
                data-testid="search-result"
                data-card-id={id}
                onClick={handleClick}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onContextMenu={handleContextMenu}
                onTouchCancel={() => {
                    if (longPressTimeoutRef.current) {
                        clearTimeout(longPressTimeoutRef.current);
                        longPressTimeoutRef.current = null;
                    }
                }}
            >
                {isSelected && <SelectionIndicator>✓</SelectionIndicator>}
                <CardText>{props.title}</CardText>
                <CardText>{props.subTitle}</CardText>
                {props.mark !== undefined && (
                    <MarkText data-testid="search-result-mark" title={props.mark.note}>
                        🚩 {markSummary(props.mark)}
                    </MarkText>
                )}
            </Card>
        </CardContainer>
    );
};
